/**
 * Catálogo ÚNICO de los estados del legajo (ciclo de vida del onboarding).
 * Fuente de verdad de cómo se muestra cada estado en el web: label claro,
 * descripción (tooltip) y tono (color del badge). Reemplaza los mapas sueltos
 * que tenía cada pantalla (Búsqueda, Dashboard, CRM TMK) con redacción distinta.
 *
 * Los valores técnicos coinciden con el enum `estado` de `legajo.entity.ts`.
 */
export type TonoEstado = 'success' | 'info' | 'warning' | 'error' | 'neutral';

export interface EstadoLegajoInfo {
  label: string;
  descripcion: string;
  tono: TonoEstado;
}

export const ESTADO_LEGAJO: Record<string, EstadoLegajoInfo> = {
  pendiente_validacion_medicus: {
    label: 'Pendiente de validación',
    descripcion: 'Medicus todavía no habilitó el legajo para el onboarding.',
    tono: 'warning',
  },
  rechazado_medicus: {
    label: 'Rechazado por Medicus',
    descripcion: 'Medicus rechazó el legajo; no avanza al onboarding.',
    tono: 'error',
  },
  creado: {
    label: 'Pendiente de onboarding',
    descripcion: 'Habilitado; el titular todavía no empezó el formulario.',
    tono: 'neutral',
  },
  en_validacion: {
    label: 'Onboarding en curso',
    descripcion: 'El titular está completando el formulario (datos, salud y plan).',
    tono: 'info',
  },
  validado_por_titular: {
    label: 'Listo para alta',
    descripcion: 'El titular terminó el onboarding; listo para enviar a Binary.',
    tono: 'info',
  },
  en_alta_binary: {
    label: 'Enviado a alta (Binary)',
    descripcion: 'Incluido en un envío de alta masiva a Binary; esperando confirmación.',
    tono: 'info',
  },
  dado_de_alta: {
    label: 'Dado de alta',
    descripcion: 'Alta confirmada en Binary; afiliado activo.',
    tono: 'success',
  },
  rechazado_binary: {
    label: 'Rechazado por Binary',
    descripcion: 'Binary rebotó el legajo por un error de dato; hay que corregir y reenviar.',
    tono: 'error',
  },
  dado_de_baja: {
    label: 'Dado de baja',
    descripcion: 'El legajo fue dado de baja (ej. fallecimiento del titular). La baja real en Binary es una etapa aparte.',
    tono: 'neutral',
  },
  legajo_bis: {
    // "bis" era jerga interna. De cara al usuario esto es gente que se suma a
    // un legajo que ya existe, y la sección donde se resuelve se llama
    // "Alta Integrantes".
    label: 'Integrantes a sumar',
    descripcion:
      'Un padrón nuevo trajo personas para un número de legajo que ya existe: se suman a ese legajo desde Alta Integrantes.',
    tono: 'warning',
  },
  // Estado SINTÉTICO (no es un estado real del legajo): lo usa la Búsqueda para
  // personas que están en un padrón en borrador y todavía no tienen legajo (se
  // crea al cerrar el padrón).
  borrador: {
    label: 'Padrón en borrador',
    descripcion: 'La persona está en un padrón que todavía no se cerró; aún no tiene legajo.',
    tono: 'neutral',
  },
};

/** Info del estado, con fallback seguro para valores desconocidos. */
export function estadoLegajoInfo(estado: string): EstadoLegajoInfo {
  return (
    ESTADO_LEGAJO[estado] ?? {
      label: estado,
      descripcion: 'Estado no reconocido.',
      tono: 'neutral',
    }
  );
}
